import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { ClientService } from './../../../../services/client.service';
import { WorkflowContextService } from './../../../../services/workflow-context.service';
import { SecurityService, SystemService } from 'src/app/services';
import { Utilities } from './../../../../services/utilities/index';

import { CustomList, CustomListItem } from './../../../../models/custom-list';
import { Role } from 'src/app/models';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';

@Component({
  selector: 'wm-settings-custom-lists',
  standalone: false,
  templateUrl: './settings-custom-lists.component.html',
  styleUrls: ['./settings-custom-lists.component.scss']
})
export class SettingsCustomListsComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  isLoading = false;
  errorMessage: string | null = null;
  customLists: CustomList[] = [];
  availableRoles: Role[] = [];
  globalCustomLists: CustomList[] = [];
  private clientSubscribe!: Subscription;
  private routerSub$!: Subscription;

  showModal = false;
  modalMode: 'new' | 'edit' = 'new';
  currentList: CustomList = this.createEmptyList();
  
  showValidation = false;
  isRoleMembersList = false;

  selectedTemplateId: string | null = null;
  isTemplateDropdownOpen: boolean = false;

  isAddingItem = false;
  isBulkAdding = false;
  editingItemIndex: number | null = null;
  newItemData = { value: '', label: '', imagePath: '', fileName: '' };
  bulkAddText = '';

  private draggedIndex: number | null = null;
  activeEditId: string | null = null;

  confirmData: CommonConfirmationModalData = {
    open: false,
    modalDescp: '',
    modalSubDescp: '',
    yesNoBtn: true,
    inTime: 300,
    outTime: 200,
    status: 'default'
  };

  private confirmAction: 'deleteItem' | 'removeCustomList' | 'toggleRole' | null = null;
  private pendingDeleteItemId: string | null = null;
  private pendingRemoveListId: string | null = null;

  roleDropdownData: any = {
    placeholder: 'Select a Role',
    options: [],
    selectedValue: null,
    style: {
      panelBg: '#ffffff',
      panelShadow: '0 4px 14px rgba(0,0,0,0.1)'
    }
  };

  constructor(
    private _context: WorkflowContextService,
    private _clientSvc: ClientService,
    private _securitySvc: SecurityService,
    private _systemSvc: SystemService,
    private _toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    if (history.state) {
      if (history.state.customLists) this.customLists = history.state.customLists;
      if (history.state.availableRoles) this.availableRoles = history.state.availableRoles;
      if (history.state.globalCustomLists) this.globalCustomLists = history.state.globalCustomLists;
    }

    if (this._context.client) {
      this.initData();
    } else {
      this.clientSubscribe = this._context.client$.subscribe(res => {
        this.initData();
      });
    }

    this.syncUrl();
    this.routerSub$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.syncUrl();
    });
  }

  ngOnDestroy(): void {
    if (this.clientSubscribe) this.clientSubscribe.unsubscribe();
    if (this.routerSub$) this.routerSub$.unsubscribe();
  }

  syncUrl() {
    const urlStr = this.router.url;
    if (urlStr.includes('/lists/add')) {
      if (this.modalMode !== 'new' || !this.showModal) {
        this.setupNewEditor();
      }
    } else if (urlStr.includes('/lists/edit/')) {
      const id = urlStr.split('/lists/edit/')[1].split(/[?#]/)[0];
      
      // Only trigger API if it's a freshly clicked ID
      if (this.activeEditId !== id) {
        this.activeEditId = id;
        this.applyExpand();
      } else if (!this.showModal) {
        this.showModal = true;
      }
    } else {
      this.closeAllEditors();
    }
  }

  initData() {
    if (!this.customLists || this.customLists.length === 0) {
      this.loadCustomLists();
    }

    if (!this.availableRoles || this.availableRoles.length === 0) {
      this._securitySvc.getRoles(this._context.client.id).subscribe(roles => {
        this.availableRoles = roles;
        this.roleDropdownData = {
          ...this.roleDropdownData,
          options: roles.map(role => ({
            label: role.name,
            value: role.id
          }))
        };
      });
    } else {
      this.roleDropdownData = {
        ...this.roleDropdownData,
        options: this.availableRoles.map(role => ({
          label: role.name,
          value: role.id
        }))
      };
    }

    if (!this.globalCustomLists || this.globalCustomLists.length === 0) {
      this._systemSvc.getGlobalCustomLists().subscribe(templates => {
          this.globalCustomLists = templates;
      });
    }
  }

  createEmptyList(): CustomList {
    return {
      id: Utilities.EMPTY_GUID,
      clientId: this._context.client ? this._context.client.id : Utilities.EMPTY_GUID,
      name: '',
      items: [],
      roleId: null
    };
  }

  loadCustomLists() {
    this.isLoading = true;
    this._clientSvc.getCustomLists(this._context.client.id).subscribe({
      next: (lists) => {
        this.customLists = lists;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'Failed to load lists.';
      }
    });
  }

  navigateToBase() {
    const baseUrl = this.router.url.split('/lists')[0] + '/lists/list';
    const stateData = { 
      customLists: this.customLists, 
      availableRoles: this.availableRoles, 
      globalCustomLists: this.globalCustomLists 
    };
    this.router.navigateByUrl(baseUrl, { state: stateData });
  }

  setupNewEditor() {
    this.activeEditId = null;
    this.modalMode = 'new';
    this.currentList = this.createEmptyList();
    this.isRoleMembersList = false;
    this.resetFormStates();
    this.showModal = true;
  }

  applyExpand() {
    if (this.activeEditId) {
      this.modalMode = 'edit';
      this.resetFormStates();
      this.isLoading = true;

      this._clientSvc.getCustomList(this.activeEditId).subscribe(fullList => {
        if (fullList) {
          this.currentList = fullList;
          this.isRoleMembersList = !!(fullList.roleId && fullList.roleId !== '');
          if (this.isRoleMembersList) {
              this.loadRoleMembers();
          }
        }
        this.isLoading = false;
        this.showModal = true;
      }, () => {
        this.isLoading = false;
        this._toastr.error('Failed to load the selected list.');
        this.navigateToBase();
      });
    }
  }

  closeAllEditors() {
    this.showModal = false;
    this.activeEditId = null;
    this.currentList = this.createEmptyList();
    this.resetFormStates();
  }

  openAddList(event?: MouseEvent) {
    if (event) event.preventDefault();
    const baseUrl = this.router.url.split('/lists')[0] + '/lists/add';
    const stateData = { 
      customLists: this.customLists, 
      availableRoles: this.availableRoles, 
      globalCustomLists: this.globalCustomLists 
    };
    this.router.navigateByUrl(baseUrl, { state: stateData });
  }

  openEditList(listSummary: CustomList) {
    const baseUrl = this.router.url.split('/lists')[0] + '/lists/edit/' + listSummary.id;
    const stateData = { 
      customLists: this.customLists, 
      availableRoles: this.availableRoles, 
      globalCustomLists: this.globalCustomLists 
    };
    this.router.navigateByUrl(baseUrl, { state: stateData });
  }

  closeModal() {
    this.navigateToBase();
  }

  resetFormStates() {
    this.isAddingItem = false;
    this.isBulkAdding = false;
    this.editingItemIndex = null;
    this.selectedTemplateId = null;
    this.isTemplateDropdownOpen = false;
    this.confirmData = { ...this.confirmData, open: false };
    this.bulkAddText = '';
    this.newItemData = { value: '', label: '', imagePath: '', fileName: '' };
    this.pendingDeleteItemId = null;
    this.pendingRemoveListId = null;
    this.confirmAction = null;
    this.showValidation = false;
  }

  saveList() {
    this.showValidation = true; 
    if (!this.currentList.name || !this.currentList.name.trim()) return;

    if (this.currentList.items.length > 0) {
      this.currentList.items.forEach((item, index) => {
        item.customListId = this.currentList.id;
        item.position = index;
      });
    }

    if (!this.isRoleMembersList) {
      this.currentList.roleId = null;
    }

    const listToSave: CustomList = {
      ...this.currentList,
      roleId: this.isRoleMembersList ? this.currentList.roleId : null
    };

    this._clientSvc.saveCustomList(listToSave).subscribe({
      next: () => {

        this._clientSvc.getCustomLists(this._context.client.id).subscribe(lists => {
          this.customLists = lists || [];
          this._toastr.success('Saved!');
          this.closeModal(); 
        }, () => {
          this._toastr.success('Saved!');
          this.closeModal();
        });
      },
      error: () => this._toastr.error('Failed to save list.')
    });
  }

  onRoleTypeClick(event: MouseEvent) {
      if (this.currentList.items.length > 0) {
          event.preventDefault(); 
          event.stopPropagation();
          
          this.confirmAction = 'toggleRole';
          this.confirmData = {
              ...this.confirmData,
              open: true,
              modalDescp: 'Changing type will clear existing list items. Continue?',
              modalSubDescp: 'This cannot be undone.',
              replaceYesWith: 'CONTINUE',
              replaceNoWith: 'CANCEL',
              status: 'default'
          };
      } else {
          this.executeRoleToggle();
      }
  }

  executeRoleToggle() {
      this.isRoleMembersList = !this.isRoleMembersList;
      this.currentList.items = [];
      if (!this.isRoleMembersList) {
          this.currentList.roleId = null;
      }
  }

  toggleTemplateDropdown() {
      this.isTemplateDropdownOpen = !this.isTemplateDropdownOpen;
  }

  selectTemplateFromDropdown(template: CustomList) {
      this.selectedTemplateId = template.id;
      this.applyTemplate();
      this.isTemplateDropdownOpen = false;
  }

  applyTemplate() {
    if (!this.selectedTemplateId) return;
    const template = this.globalCustomLists.find(t => t.id === this.selectedTemplateId);
    if (!template) return;

    const originalLength = this.currentList.items.length;
    let addedCount = 0;

    for (const templateItem of template.items) {
      const exists = this.currentList.items.some(cli => cli.value && templateItem.value && cli.value.toLowerCase() === templateItem.value.toLowerCase());
      if (!exists) {
        const itemToAdd = new CustomListItem({
          id: Utilities.generateId(),
          position: originalLength + templateItem.position,
          customListId: this.currentList.id,
          text: templateItem.text,
          value: templateItem.value,
          imagePath: templateItem.imagePath
        });
        this.currentList.items.push(itemToAdd);
        addedCount++;
      }
    }

    if(addedCount > 0) {
        this._toastr.success('Template items loaded');
    } else {
        this._toastr.info('No new items to add (duplicates skipped).');
    }
    this.selectedTemplateId = null;
  }

  requestRemoveCustomList() {
    this.pendingRemoveListId = this.currentList.id;
    this.confirmAction = 'removeCustomList';

    this.confirmData = {
      ...this.confirmData,
      open: true,
      modalDescp: `Are you sure you want to remove the list "${this.currentList.name}"?`,
      modalSubDescp: 'This action cannot be undone.',
      yesNoBtn: true,
      replaceYesWith: 'REMOVE',
      replaceNoWith: 'CANCEL',
      status: 'default' 
    };
  }

  onConfirmYes() {
    if (this.confirmAction === 'deleteItem') {
      this.executeDeleteItem();
    } else if (this.confirmAction === 'removeCustomList') {
      this.executeRemoveList();
    } else if (this.confirmAction === 'toggleRole') {
        this.executeRoleToggle();
    }
    this.confirmData = { ...this.confirmData, open: false };
  }

  onConfirmNo() {
    this.pendingDeleteItemId = null;
    this.pendingRemoveListId = null;
    this.confirmAction = null;
    this.confirmData = { ...this.confirmData, open: false };
  }

  executeRemoveList() {
    if (this.pendingRemoveListId) {
        this._clientSvc.deleteCustomList(this.pendingRemoveListId).subscribe({
            next: () => {
                this._toastr.success('Custom List Removed!');
                
                this.customLists = this.customLists.filter(l => l.id !== this.pendingRemoveListId);
                
                this.closeModal();
                this.pendingRemoveListId = null;
                this.confirmAction = null;
            },
            error: () => {
                this._toastr.error('Failed to remove custom list.');
            }
        });
    }
  }

  addNewItem() {
    this.isAddingItem = true;
    this.isBulkAdding = false;
    this.editingItemIndex = null;
    this.newItemData = { value: '', label: '', imagePath: '', fileName: '' };
  }

  editItem(item: CustomListItem, index: number) {
    this.isAddingItem = true;
    this.isBulkAdding = false;
    this.editingItemIndex = index;

    this.newItemData = {
        value: item.value,
        label: item.text, 
        imagePath: item.imagePath || '',
        fileName: item.imagePath ? 'Existing Image' : ''
    };
  }

  cancelItemForm() {
    this.isAddingItem = false;
    this.editingItemIndex = null;
    this.newItemData = { value: '', label: '', imagePath: '', fileName: '' };
  }

  toggleBulkAdd() {
    this.isBulkAdding = !this.isBulkAdding;
    this.isAddingItem = false;
    this.editingItemIndex = null;
    this.bulkAddText = '';
  }

  confirmAddItem() {
    if (!this.newItemData.value || !this.newItemData.value.trim()) return;

    if (this.editingItemIndex !== null) {
        const item = this.currentList.items[this.editingItemIndex];
        item.value = this.newItemData.value.trim();
        item.text = this.newItemData.label ? this.newItemData.label.trim() : item.value;
        item.imagePath = this.newItemData.imagePath;
        this._toastr.success('Item updated');
    } else {
        this.addItemToList(
            this.newItemData.value.trim(), 
            this.newItemData.label ? this.newItemData.label.trim() : undefined, 
            this.newItemData.imagePath
        );
        this._toastr.success("Item added");
    }
    this.cancelItemForm();
  }

  confirmBulkAdd() {
    if (!this.bulkAddText.trim()) return;

    const list = this.bulkAddText.split(',');
    let added = 0;
    list.forEach(itemStr => {
        if(!!itemStr.trim()) {
            this.addItemToList(itemStr.trim());
            added++;
        }
    });

    if (added > 0) {
        this._toastr.success(`${added} items added`);
        this.bulkAddText = '';
        this.isBulkAdding = false;
    }
  }

  addItemToList(value: string, text?: string, imagePath?: string) {
    const newItem = new CustomListItem({
        id: Utilities.generateId(),
        customListId: this.currentList.id,
        text: text || value,
        value: value,
        position: this.currentList.items.length,
        imagePath: imagePath || null
    });
    this.currentList.items.push(newItem);
  }

  requestDeleteItem(id: string, name: string) {
    this.pendingDeleteItemId = id;
    this.confirmAction = 'deleteItem';

    this.confirmData = {
        ...this.confirmData,
        open: true,
        modalDescp: `Are you sure you want to delete "${name}"?`,
        modalSubDescp: '',
        replaceYesWith: 'DELETE',
        replaceNoWith: 'CANCEL',
        status: 'default'
    };
  }

  executeDeleteItem() {
      const itemIndex = this.currentList.items.findIndex(i => i.id === this.pendingDeleteItemId);
      if (itemIndex > -1) {
          if (this.modalMode === 'edit' && this.currentList.id !== Utilities.EMPTY_GUID) {
             this._clientSvc.deleteCustomListItem(this.currentList.id, this.pendingDeleteItemId)
                .subscribe({
                    next: () => {
                        this.currentList.items.splice(itemIndex, 1);
                        this._toastr.success('Item removed');
                        this.pendingDeleteItemId = null;
                    },
                    error: () => {
                        this.currentList.items.splice(itemIndex, 1);
                        this.pendingDeleteItemId = null;
                    }
                });
          } else {
             this.currentList.items.splice(itemIndex, 1);
             this.pendingDeleteItemId = null;
          }
      }
  }

  loadRoleMembers() {
    if (this.currentList.roleId) {
      this._securitySvc.getRole(this.currentList.roleId).subscribe(r => {
        if (r && r.members) {
          const existingItemsMap = new Map(this.currentList.items.map(i => [i.roleMemberUserId || i.value, i]));

          this.currentList.items = r.members.map(m => {
            const existingItem = existingItemsMap.get(m.user.id) || existingItemsMap.get(m.user.emailAddress);
            return new CustomListItem({
              id: existingItem ? existingItem.id : Utilities.generateId(),
              customListId: this.currentList.id,
              text: m.user.name, 
              value: m.user.emailAddress,
              roleMemberUserId: m.user.id,
              position: existingItem ? existingItem.position : 0,
              imagePath: existingItem ? existingItem.imagePath : null
            });
          }).sort((a, b) => (a.text > b.text ? 1 : -1));
          
          this.currentList.items.forEach((itm, idx) => itm.position = idx);
        }
      });
    }
  }

  onDragStart(e: DragEvent, i: number) { this.draggedIndex = i; }
  onDragOver(e: DragEvent) { e.preventDefault(); }
  onDrop(e: DragEvent, dropIndex: number) {
    e.preventDefault();
    if (this.draggedIndex === null) return;
    const item = this.currentList.items[this.draggedIndex];
    this.currentList.items.splice(this.draggedIndex, 1);
    this.currentList.items.splice(dropIndex, 0, item);
    this.currentList.items.forEach((itm, idx) => itm.position = idx);
    this.draggedIndex = null;
  }

  triggerFileSelect() {
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
      this.fileInput.nativeElement.click();
    }
  }

  onImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input || !input.files || input.files.length === 0) return;
    const file = input.files[0];
    const allowed = /^image\//;
    if (!allowed.test(file.type)) {
      this._toastr.error('Please select a valid image file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.newItemData.imagePath = reader.result as string;
      this.newItemData.fileName = file.name; 
    };
    reader.onerror = () => {
      this._toastr.error('Failed to read image file.');
    };
    reader.readAsDataURL(file);
  }

  removeNewItemImage() {
    this.newItemData.imagePath = '';
    this.newItemData.fileName = '';
  }

  openImage(path: string) {
      if(!path) return;
      const imageWindow = window.open("");
      if (imageWindow) {
          imageWindow.document.write(`
            <html>
              <head><title>Image Preview</title></head>
              <body style="margin:0; display:flex; justify-content:center; align-items:center; height:100vh; background:#f0f0f0;">
                 <img src="${path}" style="max-width:100%; max-height:100%;" />
              </body>
            </html>
          `);
      }
  }
}